/**
 * Sidebar Menu Items Configuration
 *
 * THE dashboard menu, by role. The sidebar (SidebarContent) and the
 * navbar's toolbox dropdown (inc/headerMenu) both render this list, so
 * an item added here appears in both and the two cannot drift (Tyler,
 * 2026-09-10; the dropdown used to hardcode its own copy and had).
 *
 * The two roles read the same way (profile plan items 62 and 63): the
 * shared entries keep their order, the vendor-only entries sit between
 * Calendar and Spending, and the slot right above Profile holds Become A
 * Vendor for a renter. A vendor's store lives on Profile itself (item 92,
 * decision 31), so that slot is empty for them, and the entry is named
 * for the store, Store Profile (item 98; Tyler, 2026-09-13: "when a user
 * becomes a vendor they open a store"). Same path either way.
 */

import {
  MessageCircleMore,
  Calendar,
  BarChart3,
  Store,
  CalendarX2,
  ClipboardList,
  FileText,
  Package,
  Receipt,
  Banknote,
  ShieldCheck,
  CreditCard,
  Bell,
  User,
  BadgeCheck,
} from 'lucide-react'
import { FaStripeS } from 'react-icons/fa6'
import { OrderTruckIcon } from '@gearghost/icons'
import { PATHS } from '@/navigation/paths'

const P = PATHS.dashboard
const S = P.store

/**
 * Get menu items based on user role, and on the Sales Tax switch
 * (useSalesTaxEnabled), which Tax Compliance's entry follows.
 *
 * @param {string|undefined} userRole
 * @param {{ salesTaxEnabled: boolean }} options
 */
export const getSidebarMenuItems = (userRole, { salesTaxEnabled }) => {
  const baseItems = [
    {
      id: '2',
      name: 'Messages',
      link: P.messages,
      icon: <MessageCircleMore size={20} />
    },
    {
      id: '3',
      name: 'Orders',
      link: P.orders,
      // The truck is the order icon across the product — the mobile Orders
      // tab and the messaging order cards use it too. A cart here meant
      // orders and the cart shared one symbol.
      icon: <OrderTruckIcon width={20} />
    },
    {
      id: '31',
      name: 'Calendar',
      link: P.calendar,
      icon: <Calendar size={20} />
    }
  ]

  const roleSpecificItems =
    userRole !== 'vendor'
      ? []
      : [
          {
            id: '1',
            name: 'Metrics',
            link: S.metrics,
            icon: <BarChart3 size={20} />
          },
          {
            id: '51',
            name: 'Availability',
            link: S.availability,
            icon: <CalendarX2 size={20} />
          },
          {
            id: '41',
            name: 'Listings',
            link: S.listings,
            icon: <ClipboardList size={20} />
          },
          {
            id: '5',
            name: 'Inventory',
            link: S.inventory,
            icon: <Package size={20} />
          },
          // The receipts that show the tax paid on the store's gear, its
          // own page in the store menu (#495, Decisions 14 and 27), while
          // an admin has the Sales Tax switch on (2026-10-21).
          ...(salesTaxEnabled
            ? [
                {
                  id: '44',
                  name: 'Tax Compliance',
                  link: S.taxes,
                  icon: <Receipt size={20} />
                }
              ]
            : []),
          // The rental agreement, pickup instructions, and order notes
          // (profile plan item 93; Tyler, 2026-09-11).
          {
            id: '43',
            name: 'Store Documents',
            link: S.documents,
            icon: <FileText size={20} />
          },
        ]

  const commonItems = [
    {
      id: '42',
      name: 'Spending',
      link: P.spending,
      icon: <Banknote size={20} />
    },
    {
      id: '7',
      name: 'Insurance',
      link: P.insurance,
      icon: <ShieldCheck size={20} />
    },
    {
      id: '8',
      name: 'Payment methods',
      link: P.paymentMethods,
      icon: <CreditCard size={20} />
    },
    {
      id: '11',
      name: 'Notifications',
      link: P.notifications,
      icon: <Bell size={20} />
    },
    // The slot right above Profile: a renter's Become A Vendor (Tyler,
    // 2026-09-10). Once they have a store it is on Profile (item 92).
    ...(userRole !== 'vendor'
      ? [
          {
            id: '40',
            name: 'Become A Vendor',
            link: P.becomeVendor,
            icon: <Store size={20} />
          }
        ]
      : []),
    // One page, one path; a vendor's is the store's page, so it is named
    // for the store (item 98).
    {
      id: '13',
      name: userRole === 'vendor' ? 'Store Profile' : 'Profile',
      link: P.profile,
      icon: <User size={20} />
    },
    // What gates the account, one page for both perspectives (profile
    // plan item 66, decision 27).
    {
      id: '14',
      name: 'Account Standing',
      link: P.accountStanding,
      icon: <BadgeCheck size={20} />
    }
  ]

  // Add vendor-specific items if user is a vendor
  if (userRole === 'vendor') {
    roleSpecificItems.push({
      id: '9',
      name: 'Stripe Connect',
      link: S.revenue,
      icon: <FaStripeS size={18} />
    })
  }

  return [...baseItems, ...roleSpecificItems, ...commonItems]
}
